import React, { useState, useEffect } from 'react';
import { FaUser, FaBuilding, FaIdCard, FaPhone, FaEnvelope, FaEdit, FaSave, FaTimes, FaCheckCircle } from 'react-icons/fa';
import { FiClock } from 'react-icons/fi';
import './Profile.css';

interface ProfileData {
  DSSN: string;
  business_name: string;
  merchant_id: number;
  business_registration_number: string;
  tax_identification_number: string;
  email: string;
  phone_number: string;
  status: string;
  created_at: string;
}

const API_URL = process.env.REACT_APP_API_URL || 'https://api.liberianpost.com';

const Profile: React.FC = () => {
  const [profile, setProfile] = useState<ProfileData | null>(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [editData, setEditData] = useState({ phone_number: '', business_name: '' });
  const [notification, setNotification] = useState<{ show: boolean; message: string; type: string }>({
    show: false,
    message: '',
    type: ''
  });

  const token = localStorage.getItem('merchant_token');

  const showNotification = (message: string, type: 'success' | 'error' | 'info') => {
    setNotification({ show: true, message, type });
    setTimeout(() => setNotification({ show: false, message: '', type: '' }), 3000);
  };

  const fetchProfile = async () => {
    try {
      const response = await fetch(`${API_URL}/merchants/profile`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      if (data.success) {
        setProfile(data.data);
        setEditData({
          phone_number: data.data.phone_number,
          business_name: data.data.business_name
        });
      }
    } catch (error) {
      console.error('Error fetching profile:', error);
      showNotification('Failed to load profile', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateProfile = async () => {
    try {
      const response = await fetch(`${API_URL}/merchants/profile`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(editData)
      });
      const data = await response.json();
      if (data.success) {
        setProfile(data.data);
        setEditing(false);
        showNotification('Profile updated successfully', 'success');
      } else {
        showNotification(data.error || 'Update failed', 'error');
      }
    } catch (error) {
      console.error('Error updating profile:', error);
      showNotification('Failed to update profile', 'error');
    }
  };

  useEffect(() => {
    fetchProfile();
  }, []);

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString();
  };

  if (loading) {
    return (
      <div className="profile-container">
        <div className="loading-spinner"></div>
        <p>Loading profile...</p>
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="profile-container">
        <p>No profile data available</p>
      </div>
    );
  }

  return (
    <div className="profile-container">
      {notification.show && (
        <div className={`profile-notification ${notification.type}`}>
          <span>{notification.message}</span>
        </div>
      )}

      <div className="profile-header">
        <h2>
          <FaUser /> Business Profile
        </h2>
        {!editing ? (
          <button className="edit-btn" onClick={() => setEditing(true)}>
            <FaEdit /> Edit Profile
          </button>
        ) : (
          <div className="edit-actions">
            <button className="save-btn" onClick={handleUpdateProfile}>
              <FaSave /> Save
            </button>
            <button className="cancel-btn" onClick={() => {
              setEditing(false);
              setEditData({
                phone_number: profile.phone_number,
                business_name: profile.business_name
              });
            }}>
              <FaTimes /> Cancel
            </button>
          </div>
        )}
      </div>

      <div className="profile-card">
        <div className="profile-section">
          <h3>Business Information</h3>
          <div className="profile-field">
            <label>Business Name:</label>
            {editing ? (
              <input
                type="text"
                value={editData.business_name}
                onChange={(e) => setEditData({ ...editData, business_name: e.target.value })}
                className="edit-input"
              />
            ) : (
              <p><FaBuilding /> {profile.business_name}</p>
            )}
          </div>

          <div className="profile-field">
            <label>DSSN:</label>
            <p><FaIdCard /> {profile.DSSN}</p>
          </div>

          <div className="profile-field">
            <label>Merchant ID:</label>
            <p>{profile.merchant_id}</p>
          </div>

          <div className="profile-field">
            <label>Business Registration Number:</label>
            <p>{profile.business_registration_number}</p>
          </div>

          <div className="profile-field">
            <label>Tax Identification Number:</label>
            <p>{profile.tax_identification_number}</p>
          </div>
        </div>

        <div className="profile-section">
          <h3>Contact Information</h3>
          <div className="profile-field">
            <label>Email:</label>
            <p><FaEnvelope /> {profile.email}</p>
          </div>

          <div className="profile-field">
            <label>Phone Number:</label>
            {editing ? (
              <input
                type="tel"
                value={editData.phone_number}
                onChange={(e) => setEditData({ ...editData, phone_number: e.target.value })}
                className="edit-input"
              />
            ) : (
              <p><FaPhone /> {profile.phone_number}</p>
            )}
          </div>
        </div>

        <div className="profile-section">
          <h3>Account Status</h3>
          <div className="profile-field">
            <label>Status:</label>
            <p className={`status-badge ${profile.status}`}>
              {profile.status === 'active' ? <><FaCheckCircle /> Active</> : profile.status}
            </p>
          </div>

          <div className="profile-field">
            <label>Member Since:</label>
            <p><FiClock /> {formatDate(profile.created_at)}</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Profile;
